import type {
  AVERAGE_BILL_BY_FIRST_CLIENTS,
  AVERAGE_BILL_BY_SECOND_CLIENTS,
  AVERAGE_CHECK,
  AVERAGE_OCCUPANCY,
  CLIENTS_WIDGET_ID,
  FIRST_CLIENTS,
  PLAN,
  RECEIPTS_TO_CASH_REGISTER,
  RECORD_CREATED_AMOUNT,
  RECORD_CREATED_COUNT,
  RECORDS_ALL,
  RECORDS_CANCELED,
  RECORDS_CONFIRMED,
  RECORDS_WAITING,
  RETURNABILITY,
  REVENUE,
  REVENUE_BY_SERVICE,
  SECOND_CLIENTS,
} from '@app/constants';
import type { NClientServices } from '@app/types';

export namespace NAnalytics {
  export type ModelClientsGrowthFilter = {
    DateS: string;
    DateE: string;
    BranchIds: number[] | null;
  };

  export type ModelClientsGrowthResult = {
    Labels: string[];
    Values: number[];
  };

  export type ModelAnalyticsFilterData = {
    Employees: ModelEmployeeLite[] | null;
    Users: ModelUserLite[];
    ClientServiceCategories: NClientServices.ModelClientServiceCategory[];
    ClientServices: NClientServices.ModelClientServiceLite[];
  };

  export type ModelEmployeeLite = {
    Id: number;
    NameFirst: string | null;
    NameLast: string | null;
    NameFull: string | null;
    Gender: string | null;
    PositionId: number | null;
    Position: string | null;
    IsDeleted: boolean;
    IsTerminated: boolean;
    CountClientServices: number;
    WorkScheduleTill: string | null;
    IsBelongToCurrentBranch: boolean;
  };

  export type ModelUserLite = {
    Id: number;
    NameFirst: string | null;
    NameLast: string | null;
    Email: string | null;
    Gender: string | null;
    Phone: string | null;
    PositionId: number | null;
    Position: string | null;
    BranchesS: string | null;
    Photo: string | null;
    IsAdmin: boolean;
    IsDeleted: boolean;
    LastEntry: string | null;
    LastEntryS: string | null;
    Description: string | null;
    AccessRightsDescription: string | null;
    IsFakePhone: boolean;
    IsFakeEmail: boolean;
  };

  export type ModelAnalyticsWidget = {
    WidgetId: string;
    TimeCalculated: string;
    Value: string;
    ValueDifference: string | null;
    Deviation: string | null;
    Deviations: [string, string, string] | null;
  };

  export type Widget = (props: {
    isLoading: boolean;
    data: NAnalytics.ModelAnalyticsWidget | undefined;
    infoText?: string;
    title: string;
    mainValueSign?: string;
    dataWidgetId: string;
  }) => JSX.Element;

  export type ModelAnalyticsServiceCategories = {
    Categories: Category[] | null;
    TotalPrice: number;
    TotalSalePlans: SalePlanInfo;
    TotalFirstClients: ClientInfo;
    TotalSecondClient: ClientInfo;
    TotalAdditionalSales: AdditionalSales;
  };

  export type Category = {
    CategoryId: number;
    CategoryName: string | null;
    Price: number;
    PercentagePrice: number | null;
    SalePlans: SalePlanInfo;
    FirstClients: ClientInfo;
    SecondClients: ClientInfo;
    AdditionalSales: AdditionalSales;
  };

  export type SalePlanInfo = {
    Plan: number | null;
    PercentageCompletion: number | null;
    Lag: number;
  };

  export type ClientInfo = {
    Count: number | null;
    CountInBranch: number | null;
    AverageCheck: number | null;
    Amount: number | null;
  };

  export type AdditionalSales = {
    In: Info;
    Out: Info;
  };

  export type Info = {
    Count: number;
    Price: number;
  };

  export type ModelFinancialStatistic = {
    DateStart: string;
    DateEnd: string;
    SalesPlan: number;
    AmountTotal: number;
    AmountBank: number;
    AmountCash: number;
    RecordsPaidAmount: number;
    RecordsAllAmount: number;
    CountRecordVisited: number;
    CountRecord: number;
    CountClients: number;
    CountClientVisited: number;
    CountClientNoVisited: number;
    AmountNoVisited: number;
    CountRecordsNoVisited: number;
    AdditionalPaymentAmount: number;
    SalesPlanToday: number;
    PlanCompletionToday: number;
  };

  export type AnalyticsWidgetIds =
    | typeof REVENUE
    | typeof REVENUE_BY_SERVICE
    | typeof PLAN
    | typeof AVERAGE_CHECK
    | typeof AVERAGE_OCCUPANCY
    | typeof RECORDS_ALL
    | typeof RECORDS_CANCELED
    | typeof RECORDS_CONFIRMED
    | typeof RECORDS_WAITING
    | typeof RECEIPTS_TO_CASH_REGISTER
    | typeof RECORD_CREATED_COUNT
    | typeof RECORD_CREATED_AMOUNT
    | typeof FIRST_CLIENTS
    | typeof SECOND_CLIENTS
    | typeof RETURNABILITY
    | typeof AVERAGE_BILL_BY_FIRST_CLIENTS
    | typeof AVERAGE_BILL_BY_SECOND_CLIENTS
    | typeof CLIENTS_WIDGET_ID;

  export type ModelAnalyticsFilter = {
    WidgetId: AnalyticsWidgetIds;
    DateStart: string | null;
    DateEnd: string | null;
    EmployeeIds: number[] | null;
    UserIds: number[] | null;
    ServiceIds: number[] | null;
    CategoryIds: number[] | null;
    PositionIds: number[] | null;
  };

  export type ModelAnalyticsServiceCategoriesFilter = {
    DateStart: string | null;
    DateEnd: string | null;
    EmployeeIds: number[] | null;
    UserIds: number[] | null;
    CategoryIds: number[] | null;
    ServiceIds: number[] | null;
  };

  export interface WidgetConfig {
    widgetId: NAnalytics.ModelAnalyticsFilter['WidgetId'];
    setData: (data: NAnalytics.ModelAnalyticsWidget) => void;
    setLoading: (data: boolean) => void;
    title: string;
    infoText?: string;
    mainValueSign?: string;
    data?: NAnalytics.ModelAnalyticsWidget;
    isLoading: boolean;
  }
}
